import { getRelativeFontSize, theme } from "../../../utils/styles";

/** Fixed height for all banners so layout is consistent across screen sizes. */
const BANNER_HEIGHT = 180;

const userBannersStyles = {
  root: {
    width: "100%",
    maxWidth: "1200px",
    margin: "0 auto",
    paddingTop: 1,
    paddingBottom: 3,
    borderRadius: "20px",
    border: "1px solid rgba(255, 255, 255, 0.08)",
    background:
      "linear-gradient(180deg, rgba(144, 83, 246, 0.04) 0%, transparent 50%)",
    [theme.breakpoints.down("sm")]: {
      paddingTop: 0.5,
      paddingBottom: 2,
      borderRadius: "16px",
    },
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 1,
    marginBottom: 2,
    px: 2,
    [theme.breakpoints.down("sm")]: {
      marginBottom: 1.5,
    },
  },
  sectionTitleAccent: {
    width: 4,
    height: 24,
    borderRadius: "2px",
    background:
      "linear-gradient(180deg, #DF66E3 0%, #5E75FF 50%, #2CF1DF 100%)",
    [theme.breakpoints.down("sm")]: {
      height: 20,
    },
  },
  sectionTitle: {
    color: "rgba(255, 255, 255, 0.95)",
    fontSize: getRelativeFontSize(4),
    fontWeight: 600,
    textAlign: "center" as const,
    fontFamily: "Poppins_Medium",
    letterSpacing: "0.02em",
    [theme.breakpoints.down("sm")]: {
      fontSize: getRelativeFontSize(2),
    },
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(4, 1fr)",
    gap: 2,
    px: 2,
    pb: 0.5,
    [theme.breakpoints.down("lg")]: {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    [theme.breakpoints.down("sm")]: {
      gridTemplateColumns: "1fr",
      gap: 1.5,
      px: 1.5,
    },
  },
  gridCentered: {
    display: "flex",
    justifyContent: "center",
    flexWrap: "wrap" as const,
    gap: 2,
    px: 2,
    pb: 0.5,
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column" as const,
      alignItems: "stretch",
      px: 1.5,
      gap: 1.5,
    },
  },
  bannerCardCentered: {
    width: 260,
    flexShrink: 0,
    [theme.breakpoints.down("sm")]: {
      width: "100%",
    },
  },
  bannerCard: {
    height: BANNER_HEIGHT,
    borderRadius: "16px",
    overflow: "hidden",
    cursor: "pointer",
    position: "relative" as const,
    background: "transparent",
    border: "none",
    boxShadow: "none",
    transition: "transform 0.28s ease, opacity 0.28s ease",
    "&:hover": {
      transform: "scale(1.02)",
      opacity: 0.92,
    },
    "&:hover .banner-cta-pill": {
      opacity: 1,
      transform: "translateX(-50%) translateY(0)",
    },
    "&:focus": { outline: "none" },
    "&:focus-visible": {
      outline: "2px solid rgba(144, 83, 246, 0.8)",
    },
    [theme.breakpoints.down("sm")]: {
      height: 160,
    },
  },
  /** Blurred copy of the image fills the card background — no blank space */
  bannerBgBlur: {
    position: "absolute" as const,
    inset: "-8px",
    backgroundSize: "cover",
    backgroundPosition: "center",
    filter: "blur(14px) brightness(0.55) saturate(1.2)",
    transform: "scale(1.08)",
    zIndex: 0,
  },
  bannerImageWrap: {
    width: "100%",
    height: "100%",
    position: "relative" as const,
    zIndex: 1,
  },
  bannerImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain" as const,
    display: "block",
  },
  bannerOverlay: {
    position: "absolute" as const,
    bottom: 0,
    left: 0,
    right: 0,
    height: "45%",
    background:
      "linear-gradient(to top, rgba(0,0,0,0.6) 0%, rgba(0,0,0,0.15) 60%, transparent)",
    pointerEvents: "none",
  },
  /** Pill-style CTA at bottom center: gradient border + frosted fill, arrow, hover lift */
  bannerCtaWrap: {
    position: "absolute" as const,
    bottom: 14,
    left: "90%",
    transform: "translateX(-50%) translateY(6px)",
    pointerEvents: "none",
    opacity: 0.92,
    transition:
      "opacity 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease",
    [theme.breakpoints.down("sm")]: {
      bottom: 10,
      transform: "translateX(-50%) translateY(4px)",
    },
  },
  bannerCtaPill: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: 0.75,
    px: 2,
    py: 1,
    borderRadius: "999px",
    background: "rgba(255, 255, 255, 0.12)",
    backdropFilter: "blur(12px)",
    border: "1px solid rgba(255, 255, 255, 0.35)",
    boxShadow: "0 2px 12px rgba(0, 0, 0, 0.25)",
    color: "#fff",
    fontSize: getRelativeFontSize(0),
    fontWeight: 600,
    fontFamily: "Poppins_Medium",
    letterSpacing: "0.03em",
    textTransform: "uppercase" as const,
    [theme.breakpoints.down("sm")]: {
      px: 1.5,
      py: 0.75,
      fontSize: "11px",
      gap: 0.5,
    },
  },
  bannerCtaPillGradient: {
    display: "inline-flex",
    alignItems: "end",
    justifyContent: "end",
    gap: 0.75,
    px: 2,
    py: 1,
    borderRadius: "999px",
    background:
      "linear-gradient(90deg, rgba(223, 102, 227, 0.9) 0%, rgba(94, 117, 255, 0.9) 50%, rgba(44, 241, 223, 0.85) 100%)",
    border: "none",
    boxShadow: "0 2px 16px rgba(223, 102, 227, 0.35)",
    color: "#fff",
    fontSize: getRelativeFontSize(0),
    fontWeight: 700,
    fontFamily: "Poppins_Medium",
    letterSpacing: "0.04em",
    textTransform: "uppercase" as const,
    textShadow: "0 1px 2px rgba(0,0,0,0.2)",
    [theme.breakpoints.down("sm")]: {
      px: 1.5,
      py: 0.75,
      fontSize: "11px",
      gap: 0.5,
    },
  },
  bannerCtaArrow: {
    display: "inline-block",
    fontSize: "1.1em",
    lineHeight: 1,
    opacity: 0.95,
  },
  loadingBox: {
    minHeight: BANNER_HEIGHT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "100%",
  },
  emptyBox: {
    display: "none",
  },
} as const;

export default userBannersStyles;
export const BANNER_HEIGHT_PX = BANNER_HEIGHT;
